import { CurrencyPipe, NgForOf, NgIf, NgTemplateOutlet } from '@angular/common';
import { Component, Input } from '@angular/core';
import { Deduction } from '@app/interfaces';
import { Paystub } from '@app/models/payroll/company/paystub.model';
import { PlatformModule } from '@app/platform.module';
import { TranslateModule } from '@ngx-translate/core';
import { groupBy } from 'lodash-es';

// local utility type used to flag which deductions are income tax related
type DeductionWithIncomeTaxFlag = Partial<Deduction> & { isIncomeTax?: boolean };

@Component({
    selector: 'app-paystub-view-details',
    templateUrl: './paystub-view-details.template.html',
    standalone: true,
    imports: [TranslateModule, PlatformModule, CurrencyPipe, NgIf, NgForOf, NgTemplateOutlet],
    styleUrls: ['./paystub-view-details.style.scss'],
})
export class PaystubViewDetailsComponent {
    @Input({ required: true }) set paystub(paystub: Paystub) {
        this._paystub = paystub;
        this.setDeductions();
        // rows is used to iterate over both deductions and earnings as the same time.
        // It is just a zero-filled array, the length of the largest of the two arrays
        const numRows = Math.max(this.paystub.earnings?.length, this.deductions.length);
        this.rows = numRows ? new Array(numRows).fill(0) : [];
    }

    get paystub(): Paystub {
        return this._paystub;
    }

    rows: number[] = [];
    deductions: DeductionWithIncomeTaxFlag[] = [];
    private _paystub!: Paystub;

    // filters the deductions that are income tax related by adding a flag and moving them to the end of the array
    setDeductions(): void {
        const groupedDeductions = groupBy(this.paystub.deductions, (deduction) =>
            deduction.name.toUpperCase().includes('INCOME TAX') ? 'INCOME_TAX' : 'NOT_INCOME_TAX'
        );

        // Add the non-income tax related items first
        this.deductions = groupedDeductions.NOT_INCOME_TAX ?? [];

        if (groupedDeductions.INCOME_TAX?.length) {
            let incomeTaxTotal = 0;

            // Add all of the income tax related items to the end of the array, with a flag that allows the template to style them accordingly
            this.deductions = this.deductions.concat(
                groupedDeductions.INCOME_TAX.map((deduction) => {
                    // Sum the total while looping through so that we only have to iterate once
                    incomeTaxTotal += Number(deduction.current);
                    return { ...deduction, isIncomeTax: true };
                })
            );
            this.deductions.push({ displayName: 'forms.payroll-review.total.income', current: incomeTaxTotal });
        }
    }
}
